import Cookies from 'js-cookie';
import { NextRequest, NextResponse } from 'next/server';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import { getPurchaseEventId, getSignupEventId, getTrialStartEventId } from './conversionsApi';
import { marketingCookieAttributes, NINETY_DAYS_MS } from './marketingCookies';

const CLICK_ID_QUERY_PARAM_NAME = 'rdt_cid';
const CLICK_ID_COOKIE_NAME = '_rdt_cid';

// https://business.reddithelp.com/s/article/manual-conversion-events-with-the-reddit-pixel
export const trackPurchaseForReddit = ({
  userId,
  currencyCode,
  value,
  isTrial,
  subscription_event_id
}: {
  userId: number;
  currencyCode: CurrencyCode;
  value: number;
  isTrial: boolean;
  subscription_event_id?: number;
}) => {
  if (isRedditPixelUnavailable() || !subscription_event_id) {
    return;
  }

  if (isTrial) {
    rdt('track', 'TrialStart', {
      conversionId: getTrialStartEventId(subscription_event_id),
      externalId: userId
    });
  } else {
    rdt('track', 'Purchase', {
      currency: currencyCode,
      value,
      conversionId: getPurchaseEventId(subscription_event_id),
      externalId: userId
    });
  }
};

export const trackCompleteRegistrationForReddit = (userId: number, currencyCode?: CurrencyCode | null) => {
  if (isRedditPixelUnavailable()) {
    return;
  }

  rdt('track', 'SignUp', { conversionId: getSignupEventId(userId), externalId: userId, currency: currencyCode || 'USD' });
};

export const setRedditCookiesMiddleware = (request: NextRequest, response: NextResponse) => {
  const { searchParams } = request.nextUrl;
  const redditClickIdParam = searchParams.get(CLICK_ID_QUERY_PARAM_NAME);
  const ninetyDaysFromNow = new Date(Date.now() + NINETY_DAYS_MS);

  if (redditClickIdParam) {
    response.cookies.set({
      name: CLICK_ID_COOKIE_NAME,
      value: redditClickIdParam,
      expires: ninetyDaysFromNow,
      domain: process.env.APEX_DOMAIN!,
      secure: true
    });
  }
};

/**
 * Client-side counterpart to `setRedditCookiesMiddleware`, writing `_rdt_cid`.
 *
 * @param apexDomain - Apex domain to scope the cookie to, so it is readable across subdomains.
 */
export const setRedditCookiesClientSide = (apexDomain: string): void => {
  const redditClickIdParam = new URLSearchParams(window.location.search).get(CLICK_ID_QUERY_PARAM_NAME);

  if (redditClickIdParam) {
    Cookies.set(CLICK_ID_COOKIE_NAME, redditClickIdParam, marketingCookieAttributes(apexDomain));
  }
};

// Check for presence of rdt in case of content blockers
const isRedditPixelUnavailable = () => {
  return typeof rdt === 'undefined';
};
